import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, BadgeCheck, Briefcase, Lock, Sparkles, Calendar } from "lucide-react";
import { formatKES } from "../lib/api";

export default function EmployeeCard({ emp }) {
  const ir = emp.industry_rate;
  const startsSoon = emp.immediately_available;
  return (
    <Link
      to={`/employees/${emp.id}`}
      data-testid={`employee-card-${emp.id}`}
      className={`group block bg-white border rounded-md overflow-hidden hover:shadow-md transition-all hover:-translate-y-0.5 ${
        emp.is_boosted ? "border-[#7C3AED] ring-2 ring-[#7C3AED]/20" : "border-[#E4E7F5]"
      }`}
    >
      <div className="aspect-[4/3] bg-[#EEF0FA] relative overflow-hidden">
        {emp.photo_url ? (
          <img
            src={emp.photo_url}
            alt={emp.full_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-[#5B6478]">
            <Briefcase className="w-12 h-12 opacity-40" />
          </div>
        )}
        {emp.is_boosted && (
          <div
            data-testid="boosted-badge"
            className="absolute top-3 left-3 bg-gradient-to-r from-[#7C3AED] to-[#1E3A8A] text-white text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-md flex items-center gap-1 shadow-md"
          >
            <Sparkles className="w-3 h-3" /> Featured
          </div>
        )}
        {emp.verified && !emp.is_boosted && (
          <div className="absolute top-3 left-3 bg-[#1E3A8A] text-[#F8F9FE] text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-md flex items-center gap-1">
            <BadgeCheck className="w-3 h-3" /> Verified
          </div>
        )}
        {emp.tier && emp.tier !== "free" && (
          <div className="absolute top-3 right-3 bg-[#7C3AED] text-white text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-md capitalize">
            {emp.tier}
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-lg leading-tight">{emp.full_name}</h3>
            <p className="text-xs text-[#5B6478] capitalize mt-0.5">{emp.category.replace("-", " ")}</p>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <Star className="w-4 h-4 fill-[#7C3AED] text-[#7C3AED]" />
            <span className="font-semibold">{emp.rating_avg?.toFixed(1) || "—"}</span>
            <span className="text-xs text-[#5B6478]">({emp.rating_count || 0})</span>
          </div>
        </div>
        <p className="text-sm text-[#5B6478] mt-3 line-clamp-2 leading-relaxed">{emp.bio}</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {emp.available_anywhere && (
            <span className="text-[10px] uppercase tracking-widest font-semibold text-[#1E3A8A] bg-[#EEF0FA] px-2 py-0.5 rounded">
              Anywhere in Kenya
            </span>
          )}
          {startsSoon ? (
            <span className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded flex items-center gap-1">
              <Calendar className="w-2.5 h-2.5" /> Available immediately
            </span>
          ) : emp.available_from ? (
            <span className="text-[10px] uppercase tracking-widest font-semibold text-[#5B6478] bg-[#EEF0FA] px-2 py-0.5 rounded flex items-center gap-1">
              <Calendar className="w-2.5 h-2.5" /> From {emp.available_from}
            </span>
          ) : null}
        </div>
        <div className="mt-4 flex items-center justify-between pt-4 border-t border-[#E4E7F5]">
          <div className="flex items-center gap-1.5 text-xs text-[#5B6478]">
            <MapPin className="w-3 h-3" /> {emp.current_city || emp.location}
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 justify-end text-[10px] uppercase tracking-widest font-semibold text-[#5B6478]">
              <Lock className="w-2.5 h-2.5" /> Industry rate
            </div>
            {ir ? (
              <div className="font-display font-bold text-[#1E3A8A] text-sm">
                {formatKES(ir.daily_min)} – {formatKES(ir.daily_max)}<span className="text-xs text-[#5B6478] font-normal">/day</span>
              </div>
            ) : (
              <div className="text-xs text-[#5B6478]">Contact bureau</div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
